import type { MedProfile } from '../../../../pages/MedicationProfile/hook/types';
import {
  CareProgressBadgeType,
  PMMMedProfile,
  type CareProgressRowData,
  type PMMNameTypes,
} from '../../../Table/components/CareProgress/types';
import {
  convertDateInstanceToDateString,
  convertToFormattedDateString,
} from './../../../../helpers/dateHelper';
import {
  EditType,
  AdditionalDataValue,
  PatientEpisodeDetailsNames,
} from './types';

export const mapToEditType = (fieldName: string): EditType => {
  switch (fieldName) {
    case 'Planned D/C':
      return EditType.DATE;

    default:
      return EditType.TEXT;
  }
};

export const getEGFRTrend = (filteredDetails: any[]): number => {
  const eGFRValues = filteredDetails.map(
    (detail) => detail.detail?.fhir?.valueQuantity?.value,
  );
  const eGFRValuesSorted = eGFRValues.sort((a, b) => a - b);
  const trend =
    eGFRValuesSorted[eGFRValuesSorted.length - 1] - eGFRValuesSorted[0];
  return trend;
};

export const mapValueToString = (
  value: Omit<AdditionalDataValue, 'CareProgressRowData'>,
): string => {
  if (value && (typeof value === 'string' || typeof value === 'number')) {
    return value.toString();
  }
  if (value && value instanceof Date) {
    return convertDateInstanceToDateString(value, 'D MMM YY');
  }
  return '-';
};

export const mapToPrevAdmissionValue = (values?: {
  from: string;
  reason: string;
  to: string;
}): string => {
  if (!values) {
    return '-';
  }
  const { from, reason, to } = values;
  const fromDate = convertToFormattedDateString(from);
  const toDate = convertToFormattedDateString(to);
  if (fromDate && toDate) {
    return `${fromDate} - ${toDate}${reason ? ` Admitted for ${reason}` : ''}`;
  }
  return '-';
};

export const mapToPmmDocumentValue = (
  name: PMMNameTypes,
  values: any[],
): CareProgressRowData[] => {
  const results: CareProgressRowData[] = [];
  const filteredDetails = values.filter((detail) => detail.name === name);
  filteredDetails.forEach((detail) => {
    results.push({
      id: detail.id,
      event_date: detail.event_date,
      patient_id: detail.patient_id,
      name: detail.name,
      tenancy_id: detail.tenancy_id,
      detail: {
        status: detail.detail.status,
      },
      episode_id: detail.episode_id,
    });
  });

  return results;
};

export const mapToMedProfileValue = (
  medProfiles: MedProfile[],
  patientProfile: any,
  episodeDetails: any[],
) => {
  const currentMedProfile = medProfiles
    .filter((data) => data.isCurrent)
    .sort(
      (a, b) =>
        new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
    );
  const mapped: CareProgressRowData[] = currentMedProfile.map((data) => ({
    id: data.id || '',
    event_date: data?.finalisedDate || data?.updatedAt || data?.createdAt || '',
    patient_id: patientProfile.id,
    name: PMMMedProfile.MED_PROFILE,
    tenancy_id: data?.createdTenancyId,
    detail: {
      status:
        data?.profileStatus === 'finalised'
          ? CareProgressBadgeType.COMPLETED
          : data?.profileStatus === 'in progress' ||
              data?.profileStatus === 'consumer created'
            ? CareProgressBadgeType.IN_PROGRESS
            : CareProgressBadgeType.NO_DOCUMENT,
    },
    episode_id:
      episodeDetails?.[0]?.episode_id || data?.clientClinicEpisodeId || null,
  }));
  return mapped;
};

export const mapToEpisodeDetailsKey = (
  key: string,
): PatientEpisodeDetailsNames => {
  if (key === 'Admission reason') {
    return PatientEpisodeDetailsNames.AdmissionReason;
  }
  return key as PatientEpisodeDetailsNames;
};
